import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, hydrate, type DehydratedState } from '@tanstack/react-query';
import './shop.css';
import { App } from './App';
import { applyTheme, getStoredTheme } from './lib/theme';

// Apply the persisted theme before first paint (index.html ships data-theme
// 'dark'; a stored 'light' choice is applied here). Default dark.
applyTheme(getStoredTheme());

// Microsoft Clarity — same env-gated runtime injection as the driver PWA.
// VITE_CLARITY_ID is the gate AND selects the project; unset (local dev) loads
// nothing. The shop points at its own Clarity project when one exists.
const clarityId = import.meta.env['VITE_CLARITY_ID'] as string | undefined;
if (clarityId) {
  const w = window as unknown as Record<string, unknown>;
  if (!w['clarity']) {
    const fn = (...args: unknown[]): void => {
      const q = ((fn as { q?: unknown[] }).q ??= []);
      q.push(args);
    };
    w['clarity'] = fn;
  }
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.clarity.ms/tag/${clarityId}`;
  const first = document.getElementsByTagName('script')[0];
  first?.parentNode?.insertBefore(s, first);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false },
  },
});

// What the server rendered this page from (server/render.tsx), so the first
// render here matches its markup without fetching the catalog again.
const serverState = (window as unknown as { __SHOP_STATE__?: DehydratedState }).__SHOP_STATE__;
if (serverState) hydrate(queryClient, serverState);

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

const tree = (
  <StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>
);

// A page the server rendered is picked up where it stands; the client-only
// pages (cart, checkout) arrive empty and render here.
if (root.hasChildNodes()) hydrateRoot(root, tree);
else createRoot(root).render(tree);
